import React from 'react';
import ReactDOM from 'react-dom/client';

//
import './index.css';
import './styles/fonts.css';
import * as serviceWorker from './serviceWorker';
import reportWebVitals from './reportWebVitals';
import { ensureSafeStoryWebGLRuntime } from './story-runtime/runtimeWebgl';
import { renderInviteGateIfNeeded } from './inviteGate';

// ----------------------------------------------------------------------

function loadLocalOnlyScript(scriptPath) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!scriptPath) return;
  if (document.querySelector(`script[data-local-only="${scriptPath}"]`)) return;

  const script = document.createElement('script');
  script.src = scriptPath;
  script.async = true;
  script.dataset.localOnly = scriptPath;
  script.onerror = () => {
    // Local-only helpers are optional. Missing files should never block app boot.
  };
  document.head.appendChild(script);
}

loadLocalOnlyScript(process.env.REACT_APP_LOCAL_ONLY_SCRIPT || '');

function emitStoryViewerDebug(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.__STORY_VIEWER_DEBUG_HOOK__?.(payload);
  } catch (_) {
    // ignore debug hook failures
  }
}

class StoryViewerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    emitStoryViewerDebug({
      phase: 'story-viewer-react-error',
      message: String(error?.message || error),
      stack: error?.stack || null,
      componentStack: info?.componentStack || null,
    });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      return (
        <div style={{
          width: '100%',
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#f99',
          background: '#0a0a0a',
          font: '14px Menlo, monospace',
        }}
        >
          <div>Story viewer render failed.</div>
        </div>
      );
    }
    return children;
  }
}

function createRoot() {
  return ReactDOM.createRoot(document.getElementById('root'));
}

function renderApp(root = createRoot()) {
  import('./App').then((module) => {
    const App = module.default;
    root.render(<App />);
  }).catch((err) => {
    console.error('Failed to load App:', err);
    root.render(
      <div style={{
        width: '100%',
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f99',
        background: '#0a0a0a',
        font: '14px Menlo, monospace',
      }}
      >
        <div>Failed to load app.</div>
      </div>,
    );
  });
  return true;
}

function renderStoryViewer({
  src,
  minimal,
  postReady,
  postViewerState,
  postDebug,
  PanoramaViewerComponent,
}) {
  emitStoryViewerDebug({
    phase: 'render-story-viewer-enter',
    hasSrc: Boolean(src),
    minimal: Boolean(minimal),
    hasPanoramaViewerComponent: Boolean(PanoramaViewerComponent),
  });
  const root = createRoot();
  emitStoryViewerDebug({
    phase: 'story-root-created',
  });
  
  const StoryViewer = () => {
    emitStoryViewerDebug({
      phase: 'story-viewer-render',
      hasInitialSrc: Boolean(src),
      minimal: Boolean(minimal),
    });
    const [mountReceived, setMountReceived] = React.useState(window.parent === window || Boolean(src));
    const [currentSrc, setCurrentSrc] = React.useState(src);

    React.useEffect(() => {
      if (postReady) postReady();
    }, [postReady]);

    React.useEffect(() => {
      if (postDebug) {
        postDebug({
          phase: 'boot',
          href: window.location.href,
          origin: window.location.origin,
          initialSrc: src,
        });
      }

      const onMessage = (event) => {
        const data = event.data;
        if (!data || typeof data !== 'object') return;

        if (data.type === 'mount') {
          if (typeof data.src === 'string' && data.src) setCurrentSrc(data.src);
          setMountReceived(true);
          postDebug?.({
            phase: 'mount',
            href: window.location.href,
            origin: window.location.origin,
            src: typeof data.src === 'string' ? data.src : currentSrc,
          });
        } else if (data.type === 'teardown') {
          try {
            const canvas = document.querySelector('canvas');
            const gl = canvas && (
              canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false })
              || canvas.getContext('webgl', { failIfMajorPerformanceCaveat: false })
            );
            gl?.getExtension?.('WEBGL_lose_context')?.loseContext?.();
          } catch (_) {}
          try {
            window.parent?.postMessage({ type: 'teardown-ack' }, event.origin || '*');
          } catch (_) {}
        }
      };

      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }, [currentSrc, postDebug, src]);

    const handleClose = () => {
      if (window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'exit', from: 'story-viewer' }, '*');
          return;
        } catch (_) {}
      }
      window.history.back();
    };

    if (!mountReceived) {
      return (
        <div style={{
          width: '100%', height: '100dvh', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#9cf', background: '#0a0a0a', font: '14px Menlo, monospace',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div>story-viewer 等待宿主注入上下文</div>
            <div style={{ marginTop: 8, color: '#667' }}>
              正在同步 src...
            </div>
          </div>
        </div>
      );
    }

    if (!currentSrc) {
      return (
        <div style={{
          width: '100%', height: '100dvh', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#9cf', background: '#0a0a0a', font: '14px Menlo, monospace',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div>story-viewer 等待挂载</div>
            <div style={{ marginTop: 8, color: '#667' }}>
              尚未收到 mount 消息，也未在 URL 指定 ?src=...
            </div>
          </div>
        </div>
      );
    }

    if (minimal) {
      return (
        <div style={{ width: '100%', height: '100dvh', background: '#000', overflow: 'hidden' }}>
          <div style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#6cf', font: '14px Menlo, monospace',
          }}>
            <div>Story Viewer (minimal mode)</div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', height: '100dvh', background: '#000', overflow: 'hidden' }}>
        <PanoramaViewerComponent
          src={currentSrc}
          uiProfile="story"
          fitMode="fill"
          onClose={handleClose}
          onStateChange={postViewerState}
        />
      </div>
    );
  };

  root.render(
    <StoryViewerErrorBoundary>
      <StoryViewer />
    </StoryViewerErrorBoundary>,
  );
  emitStoryViewerDebug({
    phase: 'story-root-rendered',
  });
  return true;
}

if (typeof window !== 'undefined') {
  ensureSafeStoryWebGLRuntime({
    vendorName: 'StoryViewer',
    onDebug: emitStoryViewerDebug,
  });
  emitStoryViewerDebug({
    phase: 'index-entry',
    pathname: window.location.pathname,
  });
  window.__STORY_VIEWER_INIT = function init(options) {
    emitStoryViewerDebug({
      phase: 'init-registered',
      hasOptions: Boolean(options),
    });
    try {
      import('./components/PanoramaViewer').then((module) => {
        emitStoryViewerDebug({
          phase: 'panorama-module-loaded',
          hasComponent: Boolean(module?.default),
        });
        try {
          renderStoryViewer({
            ...options,
            PanoramaViewerComponent: module.default,
          });
        } catch (err) {
          emitStoryViewerDebug({
            phase: 'render-story-viewer-sync-error',
            message: String(err?.message || err),
            stack: err?.stack || null,
          });
          throw err;
        }
      }).catch((err) => {
        emitStoryViewerDebug({
          phase: 'panorama-module-error',
          message: String(err?.message || err),
          stack: err?.stack || null,
        });
        console.error('Failed to load PanoramaViewer:', err);
        const root = createRoot();
        root.render(
          <div style={{
            width: '100%', height: '100dvh', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#f99', background: '#0a0a0a', font: '14px Menlo, monospace',
          }}>
            <div>Failed to load viewer.</div>
          </div>
        );
      });
    } catch (err) {
      emitStoryViewerDebug({
        phase: 'story-init-sync-error',
        message: String(err?.message || err),
      });
      console.error('Story viewer init error:', err);
      const root = createRoot();
      root.render(
        <div style={{
          width: '100%', height: '100dvh', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#f99', background: '#0a0a0a', font: '14px Menlo, monospace',
        }}>
          <div>Init error.</div>
        </div>
      );
    }
  };
  
  if (window.location.pathname === '/embed/story-viewer.html') {
    emitStoryViewerDebug({
      phase: 'embed-branch-entered',
    });
    console.log('Story viewer mode detected');
  } else {
    emitStoryViewerDebug({
      phase: 'render-app-branch-entered',
    });
    const root = createRoot();
    renderInviteGateIfNeeded(root, () => renderApp(root)) || renderApp(root);
  }
} else {
  renderApp();
}

// If you want to enable client cache, register instead.
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
